import "./styles.css";
import { AutoRefresh, loadRefreshSeconds, saveRefreshSeconds } from "./auto-refresh";
import { shouldAutoExpandAccount } from "./account-expansion";
import { readAccountForm } from "./account-form";
import {
  ApiError,
  AccountSnapshot,
  BridgeAccount,
  CommandName,
  DeliveryStatus,
  IntentDelivery,
  IntentRecord,
  PendingOrderSnapshot,
  PositionSnapshot,
  TradeSide,
  TradingBridgeApi,
  loadApiConfig,
  resolveBackendUrl,
  saveApiConfig,
} from "./api";
import { calculateFiboPlan, decimalPlaces, FiboPlan, resolveFiboPriceInput } from "./fibo";

type ViewName = "trade" | "accounts" | "activity" | "settings";
type StrategyTab = "fibo618" | "market" | "take-profit";
type FiboInputMode = "prices" | "levels";
const selectionStorageKey = "tradingbridge.selected-accounts.v2";
const selectionModeStorageKey = "tradingbridge.selected-accounts-mode.v2";

interface DraftState {
  symbol: string;
  fiboMode: FiboInputMode;
  fiboEntry: string;
  fiboStop: string;
  fiboHigh: string;
  fiboLow: string;
  fiboRetracement: string;
  fiboSide: TradeSide;
  marketStop: string;
  marketSide: TradeSide;
  takeProfit: string;
}

const app = mustElement<HTMLDivElement>("app");
const toastRoot = mustElement<HTMLDivElement>("toast-root");
const dialogRoot = mustElement<HTMLDivElement>("dialog-root");
let config = loadApiConfig();
const api = new TradingBridgeApi(config);
let refreshSeconds = loadRefreshSeconds();
let refreshInFlight: Promise<void> | null = null;
let configRevision = 0;

const state: {
  view: ViewName;
  strategy: StrategyTab;
  accounts: BridgeAccount[];
  intents: IntentRecord[];
  selected: Set<string>;
  selectionCustomized: boolean;
  expanded: Set<string>;
  expansionCustomized: Set<string>;
  rowBusy: Set<string>;
  busy: string | null;
  backend: "unconfigured" | "connecting" | "online" | "error";
  error: string | null;
  lastSyncedAt: number | null;
  deferredRender: boolean;
  draft: DraftState;
} = {
  view: viewFromHash(),
  strategy: "fibo618",
  accounts: [],
  intents: [],
  selected: new Set(readStoredSelection()),
  selectionCustomized: localStorage.getItem(selectionModeStorageKey) === "custom",
  expanded: new Set(),
  expansionCustomized: new Set(),
  rowBusy: new Set(),
  busy: null,
  backend: api.configured ? "connecting" : "unconfigured",
  error: null,
  lastSyncedAt: null,
  deferredRender: false,
  draft: {
    symbol: "XAUUSD",
    fiboMode: "prices",
    fiboEntry: "",
    fiboStop: "",
    fiboHigh: "",
    fiboLow: "",
    fiboRetracement: "0.618",
    fiboSide: "buy",
    marketStop: "",
    marketSide: "buy",
    takeProfit: "",
  },
};

const autoRefresh = new AutoRefresh(
  () => api.configured && !document.hidden && state.view !== "settings" &&
    !state.busy && state.rowBusy.size === 0 && !isEditing() && !dialogRoot.childElementCount,
  () => refreshData(true, false),
);
autoRefresh.configure(refreshSeconds);
render();
bindEvents();
void refreshData();

import { renderApp } from "./render";
import { escapeAttr, escapeHtml, formatLots, formatPrice, orderTypeName, rowKey } from "./presentation";

function render(): void {
  state.deferredRender = false;
  app.innerHTML = renderApp(state, config, api.configured, refreshSeconds);
}

function bindEvents(): void {
  app.addEventListener("click", (event) => void handleClick(event));
  app.addEventListener("change", handleChange);
  app.addEventListener("input", handleInput);
  app.addEventListener("submit", (event) => void handleSubmit(event));
  app.addEventListener("focusout", () => {
    if (state.deferredRender) window.setTimeout(() => {
      if (canRenderRefresh()) { state.deferredRender = false; render(); }
    }, 100);
  });
  window.addEventListener("hashchange", () => { state.view = viewFromHash(); render(); });
  document.addEventListener("visibilitychange", () => { if (!document.hidden) autoRefresh.wake(); });
  window.addEventListener("online", () => autoRefresh.wake());
}

async function handleClick(event: MouseEvent): Promise<void> {
  const button = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
  if (!button) return;
  const action = button.dataset.action;
  if (action === "nav") navigate((button.dataset.view ?? "trade") as ViewName);
  if (action === "go-trade") navigate("trade");
  if (action === "go-settings") navigate("settings");
  if (action === "refresh") await refreshData();
  if (action === "strategy") { state.strategy = button.dataset.strategy as StrategyTab; render(); }
  if (action === "fibo-mode") { state.draft.fiboMode = button.dataset.mode as FiboInputMode; render(); }
  if (action === "fibo-side") { state.draft.fiboSide = button.dataset.side as TradeSide; render(); }
  if (action === "market-side") { state.draft.marketSide = button.dataset.side as TradeSide; render(); }
  if (action === "select-online") { state.selected = new Set(state.accounts.filter((a) => a.enabled && a.online).map((a) => a.id)); state.selectionCustomized = true; persistSelection(); render(); }
  if (action === "clear-selection") { state.selected.clear(); state.selectionCustomized = true; persistSelection(); render(); }
  if (action === "bulk-command") await sendBulkCommand(button.dataset.command as CommandName);
  if (action === "row-command") await sendRowCommand(button);
  if (action === "expand-account") toggleExpanded(button.dataset.accountId ?? "");
  if (action === "add-account") await addAccount();
  if (action === "account-menu") await showAccountMenu(button.dataset.accountId ?? "");
  if (action === "toggle-token") toggleTokenVisibility(button);
  if (action === "clear-token") await clearToken();
  if (action === "test-connection") await testSettingsConnection();
}

function handleChange(event: Event): void {
  const input = (event.target as HTMLElement).closest<HTMLInputElement>("[data-account-select]");
  if (!input) return;
  const id = input.dataset.accountSelect ?? "";
  input.checked ? state.selected.add(id) : state.selected.delete(id);
  state.selectionCustomized = true;
  persistSelection();
  render();
}

function handleInput(event: Event): void {
  const input = event.target as HTMLInputElement;
  if (input.id === "symbol") {
    state.draft.symbol = input.value.toUpperCase();
    document.querySelectorAll<HTMLElement>("[data-symbol-summary]").forEach((summary) => {
      summary.textContent = state.draft.symbol || "未設定";
    });
  }
  if (input.id === "fibo-entry") state.draft.fiboEntry = input.value;
  if (input.id === "fibo-stop") state.draft.fiboStop = input.value;
  if (input.id === "fibo-entry" || input.id === "fibo-stop") updateDirectFiboDirection();
  if (input.id === "fibo-high") state.draft.fiboHigh = input.value;
  if (input.id === "fibo-low") state.draft.fiboLow = input.value;
  if (input.id === "fibo-retracement") {
    state.draft.fiboRetracement = input.value;
    const summary = document.querySelector<HTMLElement>("#fibo-retracement-summary");
    if (summary) summary.textContent = input.value || "未設定";
  }
  if (input.id === "market-stop") state.draft.marketStop = input.value;
  if (input.id === "take-profit") state.draft.takeProfit = input.value;
  if (input.name === "baseUrl") updateBackendOriginPreview(input.value);
}

async function handleSubmit(event: SubmitEvent): Promise<void> {
  const form = event.target as HTMLFormElement;
  event.preventDefault();
  if (form.id === "trade-form") await submitTrade(form);
  if (form.id === "take-profit-form") await submitTakeProfit(form);
  if (form.id === "settings-form") await saveSettings(form);
}

async function submitTrade(form: HTMLFormElement): Promise<void> {
  if (!form.reportValidity() || state.selected.size === 0 || state.busy) return;
  const symbol = state.draft.symbol.trim().toUpperCase();
  if (!symbol) return showToast("請在商品折疊區輸入商品代號。", "error");
  let entryPrice = 0;
  let stopPrice = Number(state.draft.marketStop);
  let side: TradeSide = state.draft.marketSide;
  if (state.strategy === "fibo618") {
    if (state.draft.fiboMode === "prices") {
      const plan = currentDirectFiboPlan();
      if (!plan) return showToast("預計進場價與參考停損價必須是有效且不同的正數。", "error");
      entryPrice = plan.entryPrice;
      stopPrice = plan.stopPrice;
      side = plan.side;
    } else {
      const high = Number(state.draft.fiboHigh);
      const low = Number(state.draft.fiboLow);
      const retracement = Number(state.draft.fiboRetracement);
      if (!(high > 0) || !(low > 0)) return showToast("請輸入有效的最高點與最低點。", "error");
      if (high <= low) return showToast("最高點必須高於最低點。", "error");
      if (!(retracement > 0 && retracement < 1)) return showToast("回測點必須大於 0 且小於 1。", "error");
      const plan = currentFiboPlan();
      if (!plan) return showToast("無法計算 Fibo 進場價，請檢查輸入。", "error");
      entryPrice = plan.entryPrice;
      stopPrice = plan.stopPrice;
      side = state.draft.fiboSide;
    }
  }
  if (!(stopPrice > 0)) return showToast("請輸入有效的參考停損價。", "error");
  const accountIds = Array.from(state.selected);
  const selectedAccounts = state.accounts.filter((account) => state.selected.has(account.id));
  const payload = state.strategy === "fibo618"
    ? { strategy: "fibo618" as const, symbol, accountIds, entryPrice, stopPrice }
    : { strategy: "market" as const, symbol, accountIds, side, stopPrice };
  if (state.strategy === "fibo618" && (!(entryPrice > 0) || entryPrice === stopPrice)) return showToast("進場價與停損價必須有效且不同。", "error");
  const offlineAccounts = selectedAccounts.filter((account) => !account.online);
  if (!await confirmDialog({
    title: `送出${state.strategy === "fibo618" ? " Fibo進 " : " 市價進 "}意圖？`,
    body: "",
    details: [
      { label: "帳戶", value: selectedAccounts.map((account) => `${account.label}（${account.online ? "Online" : "Offline"}）`).join("、") },
      { label: "商品", value: symbol },
      { label: "方向", value: side === "buy" ? "BUY 多單" : "SELL 空單" },
      ...(state.strategy === "fibo618" && state.draft.fiboMode === "levels" ? [
        { label: "輸入模式", value: "高低點＋回測點" },
        { label: "最高點", value: formatPrice(Number(state.draft.fiboHigh)) },
        { label: "最低點", value: formatPrice(Number(state.draft.fiboLow)) },
        { label: "回測點", value: state.draft.fiboRetracement },
      ] : state.strategy === "fibo618" ? [
        { label: "輸入模式", value: "預計進場價＋參考停損價" },
      ] : []),
      { label: "進場", value: state.strategy === "fibo618" ? formatPrice(entryPrice) : "市價（EA Quote）" },
      { label: "參考停損", value: formatPrice(stopPrice) },
      { label: "有效時間", value: "後端預設" },
    ],
    confirm: "確認送出",
  })) return;
  state.busy = "trade"; render();
  try {
    await api.createTradeIntent(payload);
    showToast(`交易意圖已送至 ${accountIds.length} 個帳戶。`, "success");
    void refreshData(true, false);
  } catch (error) { handleError(error); }
  finally { state.busy = null; render(); }
}

async function submitTakeProfit(form: HTMLFormElement): Promise<void> {
  if (!form.reportValidity() || state.selected.size === 0 || state.busy) return;
  const rawValue = state.draft.takeProfit.trim();
  const takeProfit = rawValue === "" ? 0 : Number(rawValue);
  if (!Number.isFinite(takeProfit) || takeProfit < 0) {
    return showToast("停利價格必須是 0 或有效的正數。", "error");
  }
  const accountIds = Array.from(state.selected);
  const selectedAccounts = state.accounts.filter((account) => state.selected.has(account.id));
  const offlineAccounts = selectedAccounts.filter((account) => !account.online);
  const cancelling = takeProfit === 0;
  if (!await confirmDialog({
    title: cancelling ? "取消所有單子的停利？" : `將所有單子的停利設為 ${formatPrice(takeProfit)}？`,
    body: "此設定會套用到所選帳戶內的所有持倉與掛單。",
    details: [
      { label: "帳戶", value: selectedAccounts.map((account) => `${account.label}（${account.online ? "Online" : "Offline"}）`).join("、") },
      { label: "停利", value: cancelling ? "取消（0）" : formatPrice(takeProfit) },
      { label: "有效時間", value: "後端預設" },
    ],
    warning: offlineAccounts.length
      ? `含 ${offlineAccounts.length} 個 Offline 帳戶，命令可能延遲至 EA 恢復連線；超過後端設定的命令期限後不應執行。`
      : undefined,
    confirm: cancelling ? "確認取消停利" : "確認設定停利",
  })) return;
  state.busy = "set_take_profit"; render();
  try {
    await api.sendCommand({ command: "set_take_profit", takeProfit, accountIds });
    showToast(cancelling ? "取消停利命令已送出。" : "停利設定命令已送出。", "success");
    void refreshData(true, false);
  } catch (error) { handleError(error); }
  finally { state.busy = null; render(); }
}

async function sendBulkCommand(command: CommandName): Promise<void> {
  if (state.selected.size === 0 || state.busy) return;
  const isClose = command === "close_all";
  const accountIds = Array.from(state.selected);
  const selectedAccounts = state.accounts.filter((account) => state.selected.has(account.id));
  const offlineAccounts = selectedAccounts.filter((account) => !account.online);
  const confirmed = await confirmDialog({
    danger: true,
    title: isClose ? "平倉所有持倉？" : "刪除所有掛單？",
    body: ``,
    details: [
      { label: "帳戶", value: selectedAccounts.map((account) => `${account.label}（${account.online ? "Online" : "Offline"}）`).join("、") },
      { label: "有效時間", value: "後端預設" },
    ],
    warning: offlineAccounts.length
      ? `含 ${offlineAccounts.length} 個 Offline 帳戶，命令可能延遲至 EA 恢復連線；超過後端設定的命令期限後不應執行。`
      : undefined,
    confirm: isClose ? "確認全部平倉" : "確認全部刪單",
  });
  if (!confirmed) return;
  state.busy = command; render();
  try { await api.sendCommand({ command, accountIds }); showToast("批次命令已送出。", "success"); void refreshData(true, false); }
  catch (error) { handleError(error); }
  finally { state.busy = null; render(); }
}

async function sendRowCommand(button: HTMLElement): Promise<void> {
  const command = button.dataset.command as CommandName;
  const accountId = button.dataset.accountId ?? "";
  const ticket = Number(button.dataset.ticket);
  const account = state.accounts.find((item) => item.id === accountId);
  if (!account || !Number.isSafeInteger(ticket)) return;
  const deleting = command === "delete_order";
  const order = deleting
    ? account.snapshot?.pendingOrders.find((item) => item.ticket === ticket)
    : account.snapshot?.positions.find((item) => item.ticket === ticket);
  if (!order) return showToast("找不到這筆交易，請重新整理後再試。", "error");
  const type = deleting ? (order as PendingOrderSnapshot).type : (order as PositionSnapshot).side;
  if (!await confirmDialog({
    danger: true,
    title: `${deleting ? "刪除掛單" : "平倉"} #${ticket}？`,
    body: `命令只會送到「${account.label}」，由該帳戶 EA 執行。`,
    details: [
      { label: "商品", value: order.symbol },
      { label: "類型", value: orderTypeName(type) },
      { label: "Lots", value: formatLots(order.lots) },
      { label: "有效時間", value: "後端預設" },
    ],
    confirm: deleting ? "確認刪單" : "確認平倉",
  })) return;
  const key = rowKey(accountId, ticket, command);
  state.rowBusy.add(key); render();
  try { await api.sendCommand({ command, accountIds: [accountId], ticket }); showToast(`Ticket ${ticket} 命令已送出。`, "success"); void refreshData(true, false); }
  catch (error) { handleError(error); }
  finally { state.rowBusy.delete(key); render(); }
}

function refreshData(silent = false, notify = true): Promise<void> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = performRefresh(silent, notify).finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

function canRenderRefresh(): boolean {
  return !isEditing() && state.view !== "settings" && !dialogRoot.childElementCount &&
    !state.busy && state.rowBusy.size === 0;
}

async function performRefresh(silent: boolean, notify: boolean): Promise<void> {
  const revision = configRevision;
  if (!api.configured) { state.backend = "unconfigured"; if (!silent) render(); return; }
  if (!silent) {
    state.backend = "connecting";
    state.error = null;
    if (canRenderRefresh()) render();
    if (notify) showToast("正在重新整理資料…", "warning", 8_000, "refresh");
  }
  try {
    // Wait for both requests even on failure so a retry cannot overlap its sibling.
    const [accounts, intents] = await Promise.allSettled([api.getAccounts(), api.getIntents(15)]);
    if (revision !== configRevision) return;
    if (accounts.status === "rejected") throw accounts.reason;
    if (intents.status === "rejected") throw intents.reason;
    const accountsResponse = accounts.value;
    const intentsResponse = intents.value;
    state.accounts = accountsResponse.accounts;
    for (const account of state.accounts) {
      if (shouldAutoExpandAccount(account.snapshot, state.expansionCustomized.has(account.id))) {
        state.expanded.add(account.id);
      }
    }
    state.intents = intentsResponse.intents;
    const valid = new Set(state.accounts.filter((a) => a.enabled).map((a) => a.id));
    if (state.selectionCustomized) {
      state.selected.forEach((id) => { if (!valid.has(id)) state.selected.delete(id); });
    } else {
      state.selected = new Set(state.accounts.filter((account) => account.enabled && account.online).map((account) => account.id));
    }
    persistSelection();
    state.backend = "online"; state.error = null; state.lastSyncedAt = Date.now();
    if (!canRenderRefresh()) state.deferredRender = true; else render();
    if (notify) showToast(silent ? "即時資料已自動更新。" : "資料已成功刷新。", "success", silent ? 1_800 : 2_600, "refresh");
  } catch (error) {
    if (revision !== configRevision) return;
    state.backend = "error"; state.error = friendlyError(error);
    if (notify) showToast(friendlyError(error), "error", 4_500, "refresh");
    if (canRenderRefresh()) render(); else state.deferredRender = true;
  }
}

async function addAccount(): Promise<void> {
  const values = await accountFormDialog();
  if (!values) return;
  try {
    const response = await api.createAccount({ label: values.label, symbolMap: { XAUUSD: values.symbolAlias || "XAUUSD" } });
    await refreshData(true, false);
    if (response.tokenIssued && response.bridgeToken) showCredentialDialog(response.account.id, response.bridgeToken, response.account.label);
    else showToast("帳戶已存在；請輪替 Token 取得新的一次性權杖。", "warning");
  } catch (error) { handleError(error); }
}

async function showAccountMenu(id: string): Promise<void> {
  const account = state.accounts.find((item) => item.id === id);
  if (!account) return;
  const action = await choiceDialog(account);
  if (!action) return;
  try {
    if (action === "symbol") {
      const current = account.symbolMap.XAUUSD ?? "XAUUSD";
      const symbolAlias = await formDialog({
        title: "修改 XAUUSD 商品代號",
        intro: "請與 MT4 Market Watch 完全一致，包含大小寫。尚未交付的指令會使用新代號；已交付指令保留原代號。",
        fields: `<label class="field"><span>券商商品代號</span><input name="symbolAlias" maxlength="32" value="${escapeAttr(current)}" autocapitalize="none" spellcheck="false" required autofocus></label>`,
        submit: "儲存商品代號",
      }, (data) => String(data.get("symbolAlias") ?? "").trim());
      if (symbolAlias && symbolAlias !== current) {
        await api.updateAccount(id, { symbolMap: { ...account.symbolMap, XAUUSD: symbolAlias } });
        showToast("商品代號已更新，等待 EA 回報檢查結果。", "success");
      }
    }
    if (action === "rename") {
      const label = await promptDialog("重新命名帳戶", "帳戶名稱", account.label);
      if (label && label !== account.label) {
        await api.updateAccount(id, { label });
        showToast("帳戶名稱已更新。", "success");
      }
    }
    if (action === "toggle") {
      const enabled = !account.enabled;
      if (await confirmDialog({ danger: !enabled, title: `${enabled ? "啟用" : "停用"}「${account.label}」？`, body: enabled ? "啟用後可再次接收交易意圖。" : "停用後不會再列入下單帳戶。", confirm: `確認${enabled ? "啟用" : "停用"}` })) {
        await api.updateAccount(id, { enabled });
        showToast(`帳戶已${enabled ? "啟用" : "停用"}。`, "success");
      }
    }
    if (action === "rotate") {
      if (!await confirmDialog({ danger: true, title: "輪替 Bridge Token？", body: "舊 Token 會立即失效，EA 必須改用新 Token 才能繼續連線。", confirm: "產生新 Token" })) return;
      const response = await api.rotateBridgeToken(id);
      if (response.tokenIssued && response.bridgeToken) showCredentialDialog(id, response.bridgeToken, account.label);
      else showToast("本次重播不會再次顯示 Token，請重新輪替。", "warning");
    }
    if (action === "delete") {
      const confirmed = await confirmDialog({
        danger: true,
        title: `永久刪除「${account.label}」？`,
        body: "帳戶、最新狀態與該帳戶的交付紀錄會永久刪除，EA 的 Account ID／Bridge Token 也會立即失效。此操作無法復原。",
        confirm: "永久刪除",
      });
      if (!confirmed) return;
      await api.deleteAccount(id);
      state.expanded.delete(id);
      state.expansionCustomized.delete(id);
      state.selected.delete(id);
      persistSelection();
      showToast("帳戶已永久刪除。", "success");
    }
    await refreshData(true, false);
  } catch (error) { handleError(error); }
}

async function saveSettings(form: HTMLFormElement): Promise<void> {
  const data = new FormData(form);
  const nextConfig = {
    baseUrl: String(data.get("baseUrl") ?? "").trim(),
    token: String(data.get("token") ?? "").trim(),
    persistToken: data.get("persistToken") === "on",
  };
  try {
    resolveBackendUrl(nextConfig.baseUrl);
    const nextRefreshSeconds = Number(data.get("refreshSeconds"));
    saveRefreshSeconds(nextRefreshSeconds);
    saveApiConfig(nextConfig);
    configRevision += 1;
    api.setConfig(nextConfig);
    config = nextConfig;
    refreshSeconds = nextRefreshSeconds;
    autoRefresh.configure(refreshSeconds);
    state.backend = api.configured ? "connecting" : "unconfigured";
    showToast(api.configured ? "連線設定已儲存。" : "設定已儲存，尚未提供 Token。", api.configured ? "success" : "warning");
    await refreshInFlight;
    await refreshData(true, false);
  } catch (error) {
    handleError(error);
  }
}

async function testSettingsConnection(): Promise<void> {
  const form = document.querySelector<HTMLFormElement>("#settings-form");
  if (!form) return;
  const data = new FormData(form);
  try {
    const testApi = new TradingBridgeApi({ baseUrl: String(data.get("baseUrl") ?? ""), token: String(data.get("token") ?? ""), persistToken: false });
    const response = await testApi.getAccounts();
    showToast(`連線成功，找到 ${response.accounts.length} 個帳戶。`, "success");
  }
  catch (error) { handleError(error); }
}

async function clearToken(): Promise<void> {
  if (!config.token) return;
  if (!await confirmDialog({ title: "清除 Web API Token？", body: "會清除此分頁及持續保存的 Token，帳戶資料也會從畫面移除。", confirm: "確認清除" })) return;
  config = { ...config, token: "", persistToken: false };
  configRevision += 1;
  saveApiConfig(config);
  api.setConfig(config);
  state.accounts = [];
  state.expanded.clear();
  state.expansionCustomized.clear();
  state.intents = [];
  state.selected.clear();
  state.selectionCustomized = false;
  persistSelection();
  state.backend = "unconfigured";
  state.error = null;
  showToast("Web API Token 已清除。", "success");
  render();
}

function updateBackendOriginPreview(value: string): void {
  const output = document.querySelector<HTMLElement>("#backend-origin");
  if (!output) return;
  try {
    output.textContent = resolveBackendUrl(value).origin;
    output.classList.remove("invalid");
  } catch (error) {
    output.textContent = error instanceof Error ? error.message : "無效 Origin";
    output.classList.add("invalid");
  }
}

function currentDirectFiboPlan() {
  try {
    return resolveFiboPriceInput(
      Number(state.draft.fiboEntry),
      Number(state.draft.fiboStop),
    );
  } catch {
    return null;
  }
}

function updateDirectFiboDirection(): void {
  const pill = document.querySelector<HTMLElement>("#fibo-side");
  if (!pill || state.draft.fiboMode !== "prices") return;
  const plan = currentDirectFiboPlan();
  pill.className = `direction-pill ${plan?.side ?? "neutral"}`;
  pill.textContent = plan ? (plan.side === "buy" ? "BUY 多單" : "SELL 空單") : "自動判斷";
}

function currentFiboPlan(): FiboPlan | null {
  try {
    return calculateFiboPlan(
      state.draft.fiboSide,
      Number(state.draft.fiboHigh),
      Number(state.draft.fiboLow),
      Number(state.draft.fiboRetracement),
      fiboPriceDigits(),
    );
  } catch {
    return null;
  }
}

function fiboPriceDigits(): number {
  return Math.min(8, Math.max(2, decimalPlaces(state.draft.fiboHigh), decimalPlaces(state.draft.fiboLow)));
}

function accountChevron(expanded: boolean): string {
  return `<svg class="chevron-icon${expanded ? " is-expanded" : ""}" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M7 10l5 5 5-5"></path></svg>`;
}

function toggleExpanded(id: string): void { state.expansionCustomized.add(id); state.expanded.has(id) ? state.expanded.delete(id) : state.expanded.add(id); render(); }
function navigate(view: ViewName): void { state.view = view; location.hash = view; render(); window.scrollTo({ top: 0, behavior: "auto" }); }
function toggleTokenVisibility(button: HTMLElement): void { const input = button.closest(".input-shell")?.querySelector<HTMLInputElement>("input"); if (!input) return; input.type = input.type === "password" ? "text" : "password"; button.textContent = input.type === "password" ? "顯示" : "隱藏"; }

function accountFormDialog(): Promise<{ label: string; symbolAlias: string } | null> {
  return formDialog({ title: "新增 MT4 帳戶", intro: "建立後請立刻複製一次性 Bridge Token。MT4 Login 與 Broker / Server 會由第一次 EA heartbeat 自動綁定。", fields: `<label class="field"><span>帳戶名稱</span><input name="label" maxlength="80" placeholder="例如：FTMO A" required autofocus></label><label class="field"><span>XAUUSD 商品代號</span><input name="symbolAlias" maxlength="32" value="XAUUSD" autocapitalize="none" spellcheck="false" required><small>請與 MT4 Market Watch 完全一致，包含大小寫，例如 XAUUSD.s。</small></label>`, submit: "建立帳戶" }, readAccountForm);
}

function choiceDialog(account: BridgeAccount): Promise<"rename" | "symbol" | "toggle" | "rotate" | "delete" | null> {
  return new Promise((resolve) => {
    const dialog = openDialog(`<div class="dialog-head"><p class="eyebrow dark">ACCOUNT SETTINGS</p><h2>${escapeHtml(account.label)}</h2><p>${escapeHtml(account.mt4Login ?? account.id)}</p></div><div class="choice-list"><button value="rename"><span>✎</span><div><strong>重新命名</strong><small>只改變 Web 顯示名稱</small></div></button><button value="symbol"><span>⇄</span><div><strong>修改商品代號</strong><small>XAUUSD → ${escapeHtml(account.symbolMap.XAUUSD ?? "XAUUSD")}</small></div></button><button value="rotate"><span>↻</span><div><strong>輪替 Bridge Token</strong><small>舊 Token 會立即失效</small></div></button><button value="toggle" class="${account.enabled ? "danger-choice" : ""}"><span>${account.enabled ? "⊘" : "✓"}</span><div><strong>${account.enabled ? "停用帳戶" : "重新啟用"}</strong><small>${account.enabled ? "停止接收新的交易意圖" : "允許帳戶接收意圖"}</small></div></button><button value="delete" class="danger-choice"><span>×</span><div><strong>永久刪除帳戶</strong><small>清除帳戶與相關交付資料，無法復原</small></div></button></div><button class="dialog-cancel wide" value="cancel">取消</button>`);
    dialog.addEventListener("close", () => { const value = dialog.returnValue; closeAndRemove(dialog); resolve(value === "cancel" ? null : value as "rename" | "symbol" | "toggle" | "rotate" | "delete"); }, { once: true });
  });
}

function confirmDialog(options: { title: string; body: string; confirm: string; danger?: boolean; details?: Array<{ label: string; value: string }>; warning?: string }): Promise<boolean> {
  return new Promise((resolve) => {
    const details = options.details?.length
      ? `<dl class="confirm-details">${options.details.map((item) => `<div><dt>${escapeHtml(item.label)}</dt><dd>${escapeHtml(item.value)}</dd></div>`).join("")}</dl>`
      : "";
    const warning = options.warning ? `<div class="confirm-warning"><strong>延遲執行提醒</strong><p>${escapeHtml(options.warning)}</p></div>` : "";
    const dialog = openDialog(`<div class="confirm-symbol ${options.danger ? "danger" : ""}">${options.danger ? "!" : "→"}</div><div class="dialog-head centered"><h2>${escapeHtml(options.title)}</h2><p>${escapeHtml(options.body)}</p></div>${details}${warning}<div class="dialog-actions"><button class="dialog-cancel" value="cancel">取消</button><button class="dialog-confirm ${options.danger ? "danger" : ""}" value="confirm">${escapeHtml(options.confirm)}</button></div>`);
    dialog.addEventListener("close", () => { const result = dialog.returnValue === "confirm"; closeAndRemove(dialog); resolve(result); }, { once: true });
  });
}

function promptDialog(title: string, label: string, value: string): Promise<string | null> {
  return formDialog({ title, fields: `<label class="field"><span>${escapeHtml(label)}</span><input name="value" maxlength="80" value="${escapeAttr(value)}" required autofocus></label>`, submit: "儲存" }, (data) => String(data.get("value") ?? "").trim());
}

function formDialog<T>(options: { title: string; intro?: string; fields: string; submit: string }, transform: (data: FormData) => T): Promise<T | null> {
  return new Promise((resolve) => {
    const dialog = openDialog(`<form method="dialog" class="dialog-form"><div class="dialog-head"><h2>${escapeHtml(options.title)}</h2>${options.intro ? `<p>${escapeHtml(options.intro)}</p>` : ""}</div><div class="dialog-fields">${options.fields}</div><div class="dialog-actions"><button class="dialog-cancel" value="cancel">取消</button><button class="dialog-confirm" value="submit">${escapeHtml(options.submit)}</button></div></form>`);
    const form = dialog.querySelector<HTMLFormElement>("form")!;
    form.addEventListener("submit", (event) => { const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null; if (submitter?.value === "cancel") { event.preventDefault(); dialog.close("cancel"); return; } if (!form.reportValidity()) { event.preventDefault(); return; } dialog.dataset.result = "submit"; });
    dialog.addEventListener("close", () => { const result = dialog.dataset.result === "submit" ? transform(new FormData(form)) : null; closeAndRemove(dialog); resolve(result); }, { once: true });
  });
}

function showCredentialDialog(accountId: string, token: string, label: string): void {
  const dialog = openDialog(`<div class="credential-title"><span>✓</span><div><p class="eyebrow dark">ACCOUNT CREATED</p><h2>${escapeHtml(label)} 已準備好</h2></div></div><div class="one-time-warning"><strong>只顯示這一次</strong><p>關閉後無法再次取得。請立即複製到 Bridge EA 設定。</p></div><div class="credential-field"><span>Account ID</span><code>${escapeHtml(accountId)}</code><button type="button" data-copy="${escapeAttr(accountId)}">複製</button></div><div class="credential-field token"><span>Bridge Token</span><code>${escapeHtml(token)}</code><button type="button" data-copy="${escapeAttr(token)}">複製</button></div><button class="dialog-confirm wide" value="done">我已安全保存</button>`);
  dialog.addEventListener("click", (event) => { const button = (event.target as HTMLElement).closest<HTMLButtonElement>("[data-copy]"); if (!button) return; void copyText(button.dataset.copy ?? "").then(() => { button.textContent = "已複製"; }); });
  dialog.addEventListener("close", () => closeAndRemove(dialog), { once: true });
}

function openDialog(content: string): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "app-dialog";
  dialog.innerHTML = `<div class="dialog-card">${content}</div>`;
  dialog.addEventListener("click", (event) => {
    const button = (event.target as HTMLElement).closest<HTMLButtonElement>("button[value]");
    if (button?.value === "cancel") { dialog.close("cancel"); return; }
    if (button && !button.closest("form")) dialog.close(button.value);
  });
  dialogRoot.appendChild(dialog);
  dialog.showModal();
  return dialog;
}
function closeAndRemove(dialog: HTMLDialogElement): void { window.setTimeout(() => dialog.remove(), 100); }

function showToast(message: string, type: "success" | "error" | "warning" = "success", duration = 4_500, key?: string): void {
  if (key) toastRoot.querySelector<HTMLElement>(`[data-toast-key="${key}"]`)?.remove();
  const toast = document.createElement("div");
  toast.className = `toast ${type}`;
  if (key) toast.dataset.toastKey = key;
  toast.innerHTML = `<span>${type === "success" ? "✓" : type === "warning" ? "!" : "×"}</span><p>${escapeHtml(message)}</p>`;
  toastRoot.appendChild(toast);
  window.setTimeout(() => toast.remove(), duration);
}

function handleError(error: unknown, toast = true): void { const message = friendlyError(error); if (toast) showToast(message, "error"); }
function friendlyError(error: unknown): string { if (error instanceof ApiError) { if (error.status === 401) return "API Token 無效或已過期。"; if (error.status === 403) return "目前權杖沒有執行此操作的權限。"; if (error.status === 409) return `操作衝突：${error.message}`; return error.message; } return error instanceof Error ? error.message : "發生未知錯誤。"; }

function isEditing(): boolean { return document.activeElement instanceof HTMLInputElement || document.activeElement instanceof HTMLTextAreaElement || document.activeElement instanceof HTMLSelectElement; }
function viewFromHash(): ViewName { const value = location.hash.replace("#", ""); return ["trade", "accounts", "activity", "settings"].includes(value) ? value as ViewName : "trade"; }
function backendOriginForDisplay(value: string): string { try { return resolveBackendUrl(value).origin; } catch { return "無效 Origin"; } }
function persistSelection(): void {
  localStorage.setItem(selectionStorageKey, JSON.stringify(Array.from(state.selected)));
  localStorage.setItem(selectionModeStorageKey, state.selectionCustomized ? "custom" : "auto");
}
function readStoredSelection(): string[] { try { const value = JSON.parse(localStorage.getItem(selectionStorageKey) ?? "[]"); return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : []; } catch { return []; } }
async function copyText(value: string): Promise<void> { await navigator.clipboard.writeText(value); showToast("已複製到剪貼簿。", "success"); }
function mustElement<T extends HTMLElement>(id: string): T { const element = document.getElementById(id); if (!element) throw new Error(`Missing #${id}`); return element as T; }
